import React, { useCallback, useState } from "react";
import { useDropzone } from "react-dropzone";
import { useField, useFormikContext } from "formik";
import { uploadToCloudinary } from "../../lib/uploadToCloudinary";

interface Props {
  name: string;
  label?: string;
}

const DragAndDropUpload: React.FC<Props> = ({ name, label }) => {
  const [field, meta] = useField(name);
  const { setFieldValue } = useFormikContext();
  const [uploading, setUploading] = useState(false);

  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      const file = acceptedFiles[0];
      if (!file) return;

      try {
        setUploading(true);
        const imageUrl = await uploadToCloudinary(file);
        if (imageUrl) {
          setFieldValue(name, imageUrl); // ✅ store Cloudinary URL, not File
        }
      } finally {
        setUploading(false);
      }
    },
    [name, setFieldValue]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: false,
    accept: { "image/*": [] },
  });

  return (
    <div className="w-full">
      {label && <p className="text-sm font-medium mb-1">{label}</p>}

      <div
        {...getRootProps()}
        className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition 
        ${isDragActive ? "border-primary bg-primary/5" : "border-gray-300"}
        ${uploading ? "opacity-50 pointer-events-none" : ""}`}
      >
        <input {...getInputProps()} />

        {uploading ? (
          <p className="text-sm text-gray-500 flex items-center gap-2 animate-pulse">
            <span className="loader-secondary"></span>
            <span>Uploading...</span>
          </p>
        ) : field.value ? (
          <img
            src={field.value}
            alt="preview"
            className="w-24 h-24 mx-auto object-cover rounded-lg"
          />
        ) : (
          <>
            <p className="text-sm text-gray-500">Drag & drop an image here</p>
            <p className="text-xs text-gray-400 mt-1">or click to select</p>
          </>
        )}
      </div>

      {meta.touched && meta.error && (
        <p className="text-xs text-red-500 mt-1">{meta.error}</p>
      )}
    </div>
  );
};

export default DragAndDropUpload;
